import { Event, SDK, ServerOptions } from '@commercetools/frontend-sdk';
import { Product, FilterField, Category, Inventory } from 'shared/types/product';
import { ProductCardProduct } from 'shared/types/product/Product';
import { ShopCollectionQuery } from 'shared/types/query/ProductQuery';
import { PaginatedResult, ProductPaginatedResult } from 'shared/types/result';
import {
  GetAvailabilityMessageAction,
  GetProductAction,
  GetRecentlyViewedProductAction,
  GetProductSuppliesAction,
  GetBulbRecommendationsAction,
  GetRugPadRecommendationsAction,
  GetSearchableProductAttributesAction,
  GetShopCollectionAction,
  GetYouMayAlsoLikeAction,
  ProductQueryAction,
  QueryProductCategoriesAction,
  ShareProductLink,
} from '../../types/actions/ProductActions';
import { ComposableCommerceEvents } from '../../types/events/ComposableCommerceEvents';
import { ShareProductLinkPayload, YouMayAlsoLikePayload } from '../../types/payloads/ProductPayloads';
import { GetProductQuery, ProductQueryQuery, QueryProductCategoriesQuery } from '../../types/queries/ProductQueries';

export type ProductActions = {
  getProduct: GetProductAction;
  query: ProductQueryAction;
  queryCategories: QueryProductCategoriesAction;
  getSearchableAttributes: GetSearchableProductAttributesAction;
  getRecentlyViewedProduct: GetRecentlyViewedProductAction;
  getAvailabilityMessage: GetAvailabilityMessageAction;
  getShopCollection: GetShopCollectionAction;
  getYouMayAlsoLike: GetYouMayAlsoLikeAction;
  getProductSupplies: GetProductSuppliesAction;
  getBulbRecommendations: GetBulbRecommendationsAction;
  getRugPadRecommendations: GetRugPadRecommendationsAction;
  shareProductLink: ShareProductLink;
};

export const getProductActions = (sdk: SDK<ComposableCommerceEvents>): ProductActions => {
  return {
    getProduct: async (
      query: GetProductQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<Product>({
        actionName: 'product/getProduct',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError && response.data) {
        sdk.trigger(
          new Event({
            eventName: 'productFetched',
            data: {
              product: response.data,
            },
          }),
        );
      }
      return response;
    },
    query: async (
      query: ProductQueryQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<ProductPaginatedResult>({
        actionName: 'product/query',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'productsQueried',
            data: {
              query: query,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },
    queryCategories: async (
      query: QueryProductCategoriesQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<PaginatedResult<Category>>({
        actionName: 'product/queryCategories',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'productCategoriesQueried',
            data: {
              query: query,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },
    getSearchableAttributes: async (
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<FilterField[]>({
        actionName: 'product/searchableAttributes',
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'searchableProductAttributesFetched',
            data: {
              filterFields: response.data,
            },
          }),
        );
      }
      return response;
    },
    getYouMayAlsoLike: async (
      payload: YouMayAlsoLikePayload,
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<ProductCardProduct[]>({
        actionName: 'product/getYouMayAlsoLike',
        parallel: options.parallel,
        payload: {
          product: payload.product,
          selectedVariant: payload.selectedVariant,
          excludeProductKeys: payload.excludeProductKeys ?? [],
        },
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'youMayAlsoLikeQueried',
            data: {
              product: payload.product as unknown,
              result: response.data as unknown,
            },
          }),
        );
      }
      return response;
    },
    getProductSupplies: async (
      supplies: string[],
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<ProductCardProduct[]>({
        actionName: 'product/getProductSupplies',
        parallel: options.parallel,
        query: { supplies },
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'productSuppliesQueried',
            data: {
              supplies,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },
    getBulbRecommendations: async (
      bulbBase: string,
      limit: number,
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<ProductCardProduct[]>({
        actionName: 'product/getBulbRecommendations',
        parallel: options.parallel,
        query: { bulbBase, limit },
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'bulbRecommendationsQueried',
            data: {
              bulbBase,
              limit,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },
    getRugPadRecommendations: async (
      productKey: string,
      rugSize?: string,
      rugActualSize?: string,
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const query: Record<string, string> = { productKey };
      if (rugSize) query.rugSize = rugSize;
      if (rugActualSize) query.rugActualSize = rugActualSize;

      const response = await sdk.callAction<ProductCardProduct[]>({
        actionName: 'product/getRugPadRecommendations',
        parallel: options.parallel,
        query,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'rugPadRecommendationsQueried',
            data: {
              productKey,
              rugSize,
              rugActualSize,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },
    getRecentlyViewedProduct: async (
      productKey: string,
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<ProductCardProduct>({
        actionName: 'product/getRecentViewedProduct',
        parallel: options.parallel,
        query: { productKey },
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'recentlyViewedProductQueried',
            data: {
              productKey,
              result: response.data,
            },
          }),
        );
      }
      return response;
    },

    getAvailabilityMessage: async (
      sku: string,
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<Inventory>({
        actionName: 'product/getAvailabilityMessage',
        parallel: options.parallel,
        query: { sku },
      });

      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'availabilityMessageQueried',
            data: {
              sku,
              result: response.data,
            },
          }),
        );
      }

      return response;
    },

    shareProductLink: async (
      payload: ShareProductLinkPayload,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction({
        actionName: 'product/shareProductLink',
        payload,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });

      return response;
    },
    getShopCollection: async (
      query: ShopCollectionQuery,
      options: { parallel?: boolean; customHeaderValue?: string; serverOptions?: ServerOptions } = {},
    ) => {
      const response = await sdk.callAction<ProductPaginatedResult>({
        actionName: 'product/getShopCollection',
        parallel: options.parallel,
        query: { collection: query.collection, excludeSku: query.excludeSku },
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      if (!response.isError) {
        sdk.trigger(
          new Event({
            eventName: 'shopCollectionQueried',
            data: {
              query: { ...query },
              result: response.data,
            },
          }),
        );
      }

      return response;
    },
  };
};
